import React, { useEffect, useRef, useState } from 'react';
import arrowRight from '../../images/arrowRight.svg';
import './styles.css';

const ResultsSummary = ({ 
  totalResults = 0, 
  searchQuery = '', 
  currentPage = 1, 
  resultsPerPage = 10, 
  distance = '25', 
  location = '',
  providers = [], // Array of provider objects with address information
  onEditDistance = null // Callback for edit distance button
}) => {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const omsRef = useRef(null); // OverlappingMarkerSpiderfier instance
  const [mapError, setMapError] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [isOmsLoaded, setIsOmsLoaded] = useState(false);

  // Calculate the range of currently displayed results
  const startResult = totalResults > 0 ? (currentPage - 1) * resultsPerPage + 1 : 0;
  const endResult = Math.min(currentPage * resultsPerPage, totalResults);

  // Debug logging for pagination issue (US2168139) - only log when pagination values change
  // console.log('ResultsSummary calculation:', {
  //   currentPage,
  //   resultsPerPage,
  //   totalResults,
  //   startResult,
  //   endResult,
  //   shouldShow: `${startResult}-${endResult} of ${totalResults}`,
  //   providersCount: providers?.length || 0
  // });

  // Load Google Maps script
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        console.log('✅ Google Maps already loaded');
        setIsGoogleMapsLoaded(true);
        return;
      }

      // Check if script is already being loaded
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        console.log('⏳ Google Maps script already loading...');
        // Wait for it to load
        const checkInterval = setInterval(() => {
          if (window.google && window.google.maps) {
            console.log('✅ Google Maps loaded');
            setIsGoogleMapsLoaded(true);
            clearInterval(checkInterval);
          }
        }, 100);
        
        setTimeout(() => clearInterval(checkInterval), 10000); // 10 second timeout
        return;
      }

      // Get API key from runtime config (injected by server) or build-time env
      const apiKey = window.APP_CONFIG?.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        console.warn('⚠️ Google Maps API key not found in runtime config or environment variables');
        setMapError(true);
        return;
      }

      console.log('📦 Loading Google Maps script with API key from:', window.APP_CONFIG?.VITE_GOOGLE_MAPS_API_KEY ? 'runtime config' : 'build-time env');
      
      // Create and load the script
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('✅ Google Maps script loaded successfully');
        setIsGoogleMapsLoaded(true);
      };
      
      script.onerror = () => {
        console.error('❌ Failed to load Google Maps script');
        setMapError(true);
      };
      
      document.head.appendChild(script);
    };

    loadGoogleMapsScript();
  }, []);

  // Load OverlappingMarkerSpiderfier library
  useEffect(() => {
    const loadOmsScript = () => {
      // Check if OMS is already loaded
      if (window.OverlappingMarkerSpiderfier) {
        console.log('✅ OverlappingMarkerSpiderfier already loaded');
        setIsOmsLoaded(true);
        return;
      }

      // Check if script is already being loaded
      if (document.querySelector('script[src*="oms.min.js"]')) {
        console.log('⏳ OverlappingMarkerSpiderfier script already loading...');
        const checkInterval = setInterval(() => {
          if (window.OverlappingMarkerSpiderfier) {
            console.log('✅ OverlappingMarkerSpiderfier loaded');
            setIsOmsLoaded(true);
            clearInterval(checkInterval);
          }
        }, 100);
        
        setTimeout(() => clearInterval(checkInterval), 5000);
        return;
      }

      console.log('📦 Loading OverlappingMarkerSpiderfier script...');
      
      // Create and load the script
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/OverlappingMarkerSpiderfier/1.0.3/oms.min.js';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('✅ OverlappingMarkerSpiderfier script loaded successfully');
        setIsOmsLoaded(true);
      };
      
      script.onerror = () => {
        console.error('❌ Failed to load OverlappingMarkerSpiderfier script');
        // Don't set error - map will still work without spiderfier
        setIsOmsLoaded(true); // Allow map to proceed
      };
      
      document.head.appendChild(script);
    };

    if (isGoogleMapsLoaded) {
      loadOmsScript();
    }
  }, [isGoogleMapsLoaded]);

  // Initialize and update map when Google Maps is loaded and providers change
  useEffect(() => {
    if (!isGoogleMapsLoaded || !isOmsLoaded || !mapRef.current || !providers || providers.length === 0) {
      return;
    }

    console.log('🗺️ Initializing Google Map with providers:', providers.length);

    try {
      // Default center (Jacksonville, FL)
      const defaultCenter = { lat: 30.3322, lng: -81.6557 };
      
      // Initialize map if not already initialized
      if (!googleMapRef.current) {
        googleMapRef.current = new window.google.maps.Map(mapRef.current, {
          zoom: 11,
          center: defaultCenter,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            {
              // Hides ALL default Google Maps points-of-interest pins (restaurants, parks,
              // attractions, stores, etc.) — both their icons and labels — so the only pins
              // visible on the map are the provider result markers we add ourselves below.
              // Previously only "poi.business" labels were hidden, which still left every
              // POI's icon (and non-business POI labels, e.g. parks/attractions) visible.
              featureType: "poi",
              stylers: [{ visibility: "off" }]
            }
          ]
        });
        
        console.log('✅ Google Map initialized');
        
        // Initialize OverlappingMarkerSpiderfier if available
        if (window.OverlappingMarkerSpiderfier) {
          omsRef.current = new window.OverlappingMarkerSpiderfier(googleMapRef.current, {
            markersWontMove: true,
            markersWontHide: true,
            basicFormatEvents: true,
            keepSpiderfied: true,
            nearbyDistance: 30, // Pixels - markers within this distance are considered overlapping (increased for more aggressive spiderfying)
            circleSpiralSwitchover: 9, // Number of markers before switching from circle to spiral
            spiralFootSeparation: 26, // Pixels between markers in spiral
            spiralLengthStart: 15, // Starting radius of spiral
            spiralLengthFactor: 5, // How much to increase spiral per revolution
          });
          
          // Add event listener to spiderfy on mouseover (hover) instead of click
          // This makes the spiderfication feel more automatic and responsive
          omsRef.current.addListener('format', (marker, status) => {
            const icon = marker.getIcon();
            // Keep standard marker appearance but make spiderfying happen on hover
          });
          
          console.log('✅ OverlappingMarkerSpiderfier initialized with hover activation');
        }
      }

      // Clear existing markers
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];

      // Create bounds to fit all markers
      const bounds = new window.google.maps.LatLngBounds();
      let validMarkersCount = 0;

      // Add markers for each provider (limit to first 10 to avoid clutter)
      const providersToMap = providers.slice(0, 10);
      
      providersToMap.forEach((provider, index) => {
        // Build full address from provider data
        const addressParts = [
          provider.locations[0].provPhyAddr1,
          provider.locations[0].provPhyCity,
          provider.locations[0].provPhyState,
          provider.locations[0].provPhyZip
        ].filter(Boolean); // Remove null/undefined/empty values
        
        const fullAddress = addressParts.join(', ');
        
        if (!fullAddress) {
          console.warn(`⚠️ Provider ${index + 1} has no address information`, provider);
          return;
        }

        console.log(`📍 Geocoding address ${index + 1}:`, fullAddress);

        // Use Geocoding API to convert address to coordinates
        const geocoder = new window.google.maps.Geocoder();
        
        geocoder.geocode({ address: fullAddress }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const position = results[0].geometry.location;
            
            // Create marker
            const marker = new window.google.maps.Marker({
              position: position,
              map: googleMapRef.current,
              title: `${provider.firstName || ''} ${provider.lastName || ''}`.trim(),
              animation: window.google.maps.Animation.DROP,
              label: {
                text: `${index + 1}`,
                color: 'white',
                fontWeight: 'bold'
              }
            });

            // Create info window
            const providerName = `${provider.firstName || ''} ${provider.lastName || ''}`.trim();
            const specialty = Array.isArray(provider.specialty) 
              ? provider.specialty.map(s => s.specialtyName || s).join(', ')
              : provider.specialty || 'N/A';
            
            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="padding: 8px; max-width: 250px;">
                  <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #262626;">
                    ${providerName}
                  </h3>
                  <p style="margin: 0 0 4px 0; font-size: 14px; color: #666;">
                    <strong>Specialty:</strong> ${specialty}
                  </p>
                  <p style="margin: 0; font-size: 14px; color: #666;">
                    <strong>Address:</strong><br/>
                    ${fullAddress}
                  </p>
                </div>
              `
            });

            // Add click listener to show info window
            marker.addListener('click', () => {
              // Close all other info windows
              markersRef.current.forEach(m => {
                if (m.infoWindow) {
                  m.infoWindow.close();
                }
              });
              infoWindow.open(googleMapRef.current, marker);
            });
            
            // Add mouseover listener to trigger spiderfying for overlapping markers
            marker.addListener('mouseover', () => {
              // The spiderfier will automatically handle this if markers overlap
              // This ensures spiderfying happens on hover, not just click
            });

            // Store info window reference with marker
            marker.infoWindow = infoWindow;
            
            // Add marker to OverlappingMarkerSpiderfier if available
            if (omsRef.current) {
              omsRef.current.addMarker(marker);
              
              // Listen to OMS spider_click event to handle clicks on spiderfied markers
              window.google.maps.event.addListener(marker, 'spider_click', (e) => {
                // Close all other info windows
                markersRef.current.forEach(m => {
                  if (m.infoWindow) {
                    m.infoWindow.close();
                  }
                });
                infoWindow.open(googleMapRef.current, marker);
              });
            }
            
            // Add marker to array
            markersRef.current.push(marker);
            
            // Extend bounds to include this marker
            bounds.extend(position);
            validMarkersCount++;
            
            // Fit map to bounds after all markers are added
            // (This will run multiple times but that's okay)
            if (validMarkersCount > 0) {
              if (validMarkersCount === 1) {
                // If only one marker, center on it with a reasonable zoom
                googleMapRef.current.setCenter(position);
                googleMapRef.current.setZoom(14);
              } else {
                // If multiple markers, fit to bounds
                googleMapRef.current.fitBounds(bounds);
              }
            }
            
            console.log(`✅ Marker ${index + 1} added successfully`);
          } else {
            console.error(`❌ Geocoding failed for address ${index + 1}:`, status, fullAddress);
          }
        });
      });

      console.log(`🗺️ Map update complete. Processing ${providersToMap.length} addresses...`);
      
    } catch (error) {
      console.error('❌ Error initializing Google Map:', error);
      setMapError(true);
    }
  }, [isGoogleMapsLoaded, isOmsLoaded, providers]);

  // Cleanup markers on unmount
  useEffect(() => {
    return () => {
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
    };
  }, []);

  return (
    <div className="results-summary">
      {/* Main title with total results and search query */}
      {/* id + tabIndex=-1: lets results.jsx programmatically focus this heading once
          results finish loading, so keyboard/screen-reader users landing on this page
          land directly on "what's relevant" instead of staying focused at the very top
          of the page (search bar/filters) with the actual results further down,
          requiring a manual scroll to even notice they've arrived. Not in the natural
          tab order (tabIndex=-1) — only reachable via that programmatic .focus() call. */}
      <h2 className="results-summary-title" id="results-summary-heading" tabIndex={-1}>
        {totalResults} result{totalResults !== 1 ? 's' : ''} for "{searchQuery}"
      </h2>
      
      {/* Results range and location info */}
      <p className="results-summary-details">
        {totalResults > 0 ? (
          <>
            {startResult}-{endResult} of {totalResults} results within {distance} miles of {location}
          </>
        ) : (
          `0 results within ${distance} miles of ${location}`
        )}
        {onEditDistance && (
          <button 
            type="button" 
            className="edit-distance-link" 
            onClick={onEditDistance}
            aria-label="Edit distance"
          >
            Edit distance <img src={arrowRight} className="arrowRight" alt="" aria-hidden="true"/>
          </button>
        )}
      </p>

      {/* Google Maps Container */}
      <div className="map-container">
        {mapError ? (
          <div className="map-placeholder">
            <div className="map-content">
              <span className="map-label map-error">
                ⚠️ Unable to load map. Please configure Google Maps API key.
              </span>
            </div>
          </div>
        ) : !isGoogleMapsLoaded ? (
          <div className="map-placeholder">
            <div className="map-content">
              <span className="map-label">
                📍 Loading map...
              </span>
            </div>
          </div>
        ) : providers && providers.length > 0 ? (
          <div 
            ref={mapRef} 
            className="google-map"
            role="region"
            aria-label="Map showing provider locations"
          />
        ) : (
          <div className="map-placeholder">
            <div className="map-content">
              <span className="map-label">
                📍 No provider locations to display
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsSummary;